import { useEffect, useRef } from 'react';
import { useUIStore } from '@/store/uiStore';
import CommandPalette from '@/app/components/common/CommandPalette';
import { useSessionStore } from '@/store/sessionStore';
import { useContextMenuTemplateStore } from '@/store/contextTemplateStore';
import { useSystemPromptStore } from '@/store/systemPromptStore';
import { useCustomSnippetStore } from '@/store/customSnippetStore';
import { setAsActiveUi } from '@/lib/api';
import { useContextSnippetStore } from '@/store/contextSnippetStore';
import { useCustomVariableStore } from '@/store/customVariableStore';
import { useAgentStore } from '@/store/agentStore';
import { VariableToggleModal } from './VariableToggleModal';
import { connectWebSocket, disconnectWebSocket } from '@/lib/websocket';


const AppInitializer = () => {
  const { toggleCommandPalette } = useUIStore();
  const { loadActiveSession, startNewSession, currentSessionId } = useSessionStore();
  const { openModal: openVariableModal, closeModal: closeVariableModal, isModalOpen: isVariableModalOpen, isEditMode: isVariableModalEditMode, toggleVariable: toggleVariableAction, variables } = useCustomVariableStore();

  // Hotkeys for variable modal
  const lastCtrlPress = useRef(0);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Use e.code for physical key presses, more reliable across layouts/modifiers
      if (e.code === 'KeyP' && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        toggleCommandPalette();
      }
      if (e.code === 'KeyR' && e.altKey) {
        e.preventDefault();
        void loadActiveSession();
      }
      if (e.code === 'KeyN' && (e.altKey) && e.shiftKey) {
        e.preventDefault();
        void startNewSession({ session_title: "New Session" });
      }

      // Variable Modal Hotkeys
      if (e.key === 'Control') {
        const now = Date.now();
        if (now - lastCtrlPress.current < 300) {
          e.preventDefault();
          if (isVariableModalOpen) {
            closeVariableModal();
          } else {
            openVariableModal();
          }
        }
        lastCtrlPress.current = now;
      }

      if (isVariableModalOpen) {
        if (e.code === 'Escape') {
          e.preventDefault();
          closeVariableModal();
        }

        // Only allow numeric toggling if NOT in edit mode
        if (!isVariableModalEditMode) {
            const numericKey = parseInt(e.key, 10);
            if (!isNaN(numericKey) && numericKey >= 1 && numericKey <= 9) {
              e.preventDefault();

              // Match "Simple" view numbering: Sort by order, filter by freq used
              const sortedVariables = [...variables].sort((a, b) => a.order_number - b.order_number);
              const simpleVariables = sortedVariables.filter(v => v.is_freq_used);
              const variable = simpleVariables[numericKey - 1];

              if (variable) {
                  void toggleVariableAction(variable.id, { enabled: !variable.enabled });
              }
            }
        }
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [toggleCommandPalette, loadActiveSession, startNewSession, currentSessionId, isVariableModalOpen, isVariableModalEditMode, openVariableModal, closeVariableModal, toggleVariableAction, variables]);

  // WebSocket connection — connect on mount, disconnect on unmount
  // When bport changes (via switchToPort), a custom event triggers reconnection
  useEffect(() => {
    connectWebSocket();

    const handlePortSwitch = () => {
      connectWebSocket();
    };

    // When the tab becomes visible again, reconcile streaming state in case
    // completion events were missed while the tab was backgrounded/frozen
    // (the WebSocket can drop, and the backend broadcasts have no replay).
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        useSessionStore.getState().reconcileStreamingState();
      }
    };

    window.addEventListener('bport-change', handlePortSwitch);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('bport-change', handlePortSwitch);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disconnectWebSocket();
    };
  }, []);

  // Prefetch data for command palette and variable modal
  const hasInitialized = useRef(false);
  
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    
    void useSessionStore.getState().fetchSessions('non-empty');
    void useContextMenuTemplateStore.getState().fetchTemplates();
    void useSystemPromptStore.getState().fetchSystemPrompts();
    void useCustomSnippetStore.getState().fetchSnippets();
    void useContextSnippetStore.getState().fetchSnippets();
    void useCustomVariableStore.getState().fetchVariables();
    void useAgentStore.getState().fetchAgents();
  }, []);

  // Notify backend that this is active UI (only once per mount)
  const hasRegistered = useRef(false);
  
  useEffect(() => {
    if (hasRegistered.current) return;
    hasRegistered.current = true;
    
    setAsActiveUi().catch(() => {
      // Fire-and-forget
    });
  }, []);

  return (
    <>
      <CommandPalette />
      <VariableToggleModal />
    </>
  );
};

export default AppInitializer;